import { Avatar, Dropdown, Label } from "@heroui/react";
import {
    SunIcon,
    MoonIcon,
    ArrowRightStartOnRectangleIcon,
    SwatchIcon
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router";
import { signOut } from "@/services/auth";
import { useAuthContext } from "@/features/authentication/useAuthContext";

interface AvatarMenuProps {
    theme: "light" | "dark";
    onToggleTheme: () => void;
}

export const AvatarMenu = ({ theme, onToggleTheme }: AvatarMenuProps) => {
    const { user } = useAuthContext();
    const navigate = useNavigate();
    const ThemeIcon = theme === "light" ? MoonIcon : SunIcon;
    const showDesignSystem = import.meta.env.DEV;

    const handleAction = async (key: React.Key) => {
        const k = String(key);
        if (k === "theme") return onToggleTheme();
        if (k === "design-system") return navigate("/design-system");
        if (k === "logout") return signOut();
    };

    return (
        <Dropdown>
            <Dropdown.Trigger
                aria-label="Account menu"
                className="rounded-full ring-1 ring-secondary-300 hover:ring-secondary-400 transition-all cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary-500"
            >
                <Avatar size="sm">
                    <Avatar.Fallback className="!bg-secondary-200 !text-secondary-800">
                        {user?.email?.[0]?.toUpperCase() ?? "?"}
                    </Avatar.Fallback>
                </Avatar>
            </Dropdown.Trigger>
            <Dropdown.Popover placement="bottom end">
                <Dropdown.Menu
                    aria-label="Profile"
                    disabledKeys={["profile"]}
                    onAction={handleAction}
                >
                    <Dropdown.Item key="profile" id="profile" textValue="Signed in" className="h-14 gap-2">
                        <div>
                            <p className="text-xs text-default-700">Signed in as</p>
                            <p className="text-sm font-medium text-foreground truncate max-w-[220px]">{user?.email}</p>
                        </div>
                    </Dropdown.Item>
                    <Dropdown.Item key="theme" id="theme" textValue={`Switch to ${theme === "light" ? "dark" : "light"} theme`}>
                        <div className="flex items-center gap-2">
                            <ThemeIcon className="w-4 h-4" />
                            <Label>Switch to {theme === "light" ? "dark" : "light"} theme</Label>
                        </div>
                    </Dropdown.Item>
                    {showDesignSystem ? (
                        <Dropdown.Item key="design-system" id="design-system" textValue="Design system">
                            <div className="flex items-center gap-2">
                                <SwatchIcon className="w-4 h-4" />
                                <Label>Design system</Label>
                            </div>
                        </Dropdown.Item>
                    ) : null}
                    <Dropdown.Item key="logout" id="logout" textValue="Sign out" variant="danger">
                        <div className="flex items-center gap-2">
                            <ArrowRightStartOnRectangleIcon className="w-4 h-4" />
                            <Label>Sign out</Label>
                        </div>
                    </Dropdown.Item>
                </Dropdown.Menu>
            </Dropdown.Popover>
        </Dropdown>
    );
};
